import { initializeApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
} from "firebase/firestore";
import {
  connectStorageEmulator,
  getDownloadURL,
  getStorage,
  ref,
  StorageReference,
  uploadBytesResumable,
} from "firebase/storage";

const useFirebaseEmulators =
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";
const sandboxMode = process.env.REACT_APP_WAVEGEN_SANDBOX === "true";
const sandboxProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;

if (
  sandboxMode &&
  (!useFirebaseEmulators ||
    !sandboxProjectId?.startsWith("demo-") ||
    process.env.REACT_APP_WORKSPACES_ENABLED !== "true")
) {
  throw new Error(
    "WaveGen sandbox requires workspaces, Firebase emulators, and a demo-* Firebase project.",
  );
}

const firebaseConfig = {
  apiKey: "AIzaSyDXaWB-Xq9T6OqmUNq1iFgWCqQiVsIHQhM",
  projectId: sandboxProjectId || "wavegen-4f28f",
  appId: "1:261045842665:web:5d8159246ef241b78a7678",
  serviceAccountId:
    "firebase-adminsdk-iob9n@wavegen-4f28f.iam.gserviceaccount.com",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    "wavegen-4f28f.appspot.com",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA Enterprise, invisible). Currently monitor-only —
// enforcement is off in the Firebase console, so a failed attestation never
// blocks a user. No-op when the site key env var is absent (dev, E2E).
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
if (appCheckSiteKey) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.error("[AppCheck] init failed", e);
  }
}

export const auth = getAuth(app);

export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
});
export const storage = getStorage(app);

if (useFirebaseEmulators) {
  const authHost = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
  const firestoreHost = process.env.REACT_APP_FIRESTORE_EMULATOR_HOST;
  const storageHost = process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_HOST;

  if (!authHost || !firestoreHost || !storageHost) {
    throw new Error("Firebase emulator hosts are required in sandbox mode.");
  }

  const [firestoreHostname, firestorePort] = firestoreHost.split(":");
  const [storageHostname, storagePort] = storageHost.split(":");
  connectAuthEmulator(auth, `http://${authHost}`, { disableWarnings: true });
  connectFirestoreEmulator(db, firestoreHostname, Number(firestorePort));
  connectStorageEmulator(storage, storageHostname, Number(storagePort));
}

export function isFirebaseStorageUrl(value: string): boolean {
  return (
    value.includes("firebasestorage.googleapis.com") ||
    value.includes("storage.googleapis.com")
  );
}

/**
 * True when a Firebase call failed because the browser couldn't reach
 * Google's servers (offline, blocked by an extension or firewall), not
 * because the request itself was bad. Auth gives up after 30s; every Storage
 * read needs a fresh token, so this surfaces from getSignedUrl too.
 */
export function isFirebaseNetworkError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return (
    code === "auth/network-request-failed" ||
    code === "storage/retry-limit-exceeded"
  );
}

export async function getSignedUrl(path: string): Promise<string> {
  if (!path) {
    throw new Error(`getSignedUrl called with empty path: "${path}"`);
  }
  if (path.startsWith("blob:") || path.startsWith("data:")) return path;
  if (
    path.startsWith("http") &&
    !isFirebaseStorageUrl(path)
  ) {
    return path;
  }
  return getDownloadURL(ref(storage, path));
}

/**
 * Uploads a Blob to Firebase Storage using uploadBytesResumable
 * and returns the gs:// storage path upon success.
 *
 * @param blob The Blob data to upload (e.g., from canvas.toBlob).
 * @param storagePath The desired destination path in Firebase Storage
 *                    (e.g., 'users/uid/posts/pid/overlays/oid.png').
 * @returns A Promise that resolves with the gs:// path string on success, or null on failure.
 */
export async function uploadBlobToFirebase(
  blob: Blob,
  storagePath: string
): Promise<string | null> {
  if (!blob || !storagePath) {
    console.error(
      "[Firebase Upload] Invalid arguments: Blob or storagePath missing."
    );
    return null;
  }

  try {
    const storageRef: StorageReference = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, blob);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Optional: Track progress if needed elsewhere
          // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // console.log('Upload is ' + progress + '% done');
          switch (snapshot.state) {
            case "paused":
              // console.log('Upload is paused');
              break;
            case "running":
              // console.log('Upload is running');
              break;
          }
        },
        (error) => {
          // Handle unsuccessful uploads
          console.error(
            `[Firebase Upload] Failed for path ${storagePath}:`,
            error
          );
          reject(error); // Reject the promise on error
        },
        async () => {
          // Handle successful uploads on complete
          try {
            const downloadURL = await getSignedUrl(storagePath);
            resolve(downloadURL);
          } catch (metaError) {
            console.error(
              `[Firebase Upload] Failed to get download URL or metadata after upload for ${storagePath}:`,
              metaError
            );
            reject(metaError); // Reject if metadata retrieval fails
          }
        }
      );
    });
  } catch (error) {
    console.error(
      `[Firebase Upload] Error initiating upload for ${storagePath}:`,
      error
    );
    return null; // Return null if setup fails
  }
}

export default app;
